/**
 * Probá tu bot (GKTIBO-75).
 *
 * ## Lo que hace útil a esta pantalla no es el chat
 *
 * El bot ya responde en WhatsApp. Lo que acá se ve y en ningún otro lado es
 * **de dónde salió cada respuesta** — y sobre todo cuando no salió de ningún
 * lado.
 *
 * El 2026-09-21 el bot dijo «ya avisé al equipo de urgencia» tres veces
 * seguidas sin llamar a ninguna herramienta. Se descubrió leyendo los logs de
 * la Lambda, un día después. Es GKTIBO-44 —el bot prometiendo lo que no puede
 * cumplir— y hoy sólo se detecta cuando ya le pasó a un cliente.
 *
 * Acá se ve en el momento, y lo juzga quien mejor puede: el dueño sabe si su
 * negocio coordina para mañana o no.
 *
 * ## Por qué NO es un paso que bloquea la activación
 *
 * No hay señal en el servidor de «ya probé», y el progreso del alta no se
 * guarda en el navegador a propósito. Marcar el paso como hecho sin esa señal
 * sería mentirle al indicador. Y peor: si esta prueba fallara, el dueño
 * quedaría sin poder poner su bot en vivo por un problema nuestro.
 *
 * Vive sobre el botón de activar, y con ruta propia en el panel — porque
 * después de salir en vivo el dueño va a cambiar su conocimiento y va a querer
 * probarlo otra vez (C7).
 */

import { useMutation } from '@tanstack/react-query';
import { SendHorizontal, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api.ts';
import { DeDondeSalio } from './DeDondeSalio';
import { SugerenciasDePrueba } from './SugerenciasDePrueba';
import { TextoDelBot } from './TextoDelBot';
import { ValorarRespuesta } from './ValorarRespuesta';
import { probar as t } from './textos-probar';
import { type Turno, nuevaSesion } from './turnos-de-la-prueba';

export interface ProbarElBotProps {
  tenantId: string;
  /** Lo que el dueño cargó como servicios, para sugerir qué probar. */
  servicios?: string[];
  /**
   * Las herramientas que su bot tiene habilitadas.
   *
   * ⚠️ Hoy el panel no las sabe **antes** del primer mensaje: el config del
   * tenant no lista herramientas, y el mapa modo→herramientas vive en el
   * backend. Así que se recibe si el padre la sabe, y si no se aprende de la
   * primera respuesta —que las devuelve— y la sugerencia de reclamo aparece
   * recién ahí. Queda dicho en vez de fingir que siempre están.
   */
  herramientas?: string[];
  /**
   * El dueño ya habló con su bot al menos una vez (GKTIBO-123).
   *
   * Habilita «Continuar» en el paso 2. Se avisa desde acá y no desde el
   * servidor porque el padre no tiene forma de enterarse: el estado del alta
   * no se vuelve a consultar solo con el conocimiento ya indexado.
   */
  onProbado?: () => void;
}

export function ProbarElBot({
  tenantId,
  servicios = [],
  herramientas = [],
  onProbado,
}: ProbarElBotProps) {
  const [sesionId] = useState(nuevaSesion);
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [texto, setTexto] = useState('');
  const [restantes, setRestantes] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conQue, setConQue] = useState<string[]>(herramientas);
  const finDelHilo = useRef<HTMLDivElement>(null);
  const siguienteId = useRef(0);
  /** Lo último que el dueño preguntó: es lo que se corrige. */
  const ultimaPregunta = useRef('');

  /**
   * Que el último turno quede a la vista.
   *
   * `scrollIntoView` se llama de forma opcional a propósito —mismo criterio
   * que la entrevista—: no existe en todos los entornos, y que la prueba
   * **deje de funcionar** porque no se pudo desplazar sería cambiar un detalle
   * visual por la pantalla entera.
   */
  const alFinal = () => finDelHilo.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });

  /**
   * ⚠️ El efecto NO devuelve nada, a propósito.
   *
   * `useEffect(alFinal, [])` devolvía lo que devolviera `scrollIntoView`, y
   * React usa ese valor **como función de limpieza al desmontar**. Si no es
   * una función, tira `destroy is not a function` —minificado, «n is not a
   * function»— y **se cae el árbol entero**: pantalla en blanco.
   *
   * Nunca se había notado porque este chat no se desmontaba jamás, hasta que
   * GKTIBO-116 le puso el `key` que lo remonta al cambiar de negocio.
   */
  useEffect(() => {
    // La llamada va INLINE, sin pasar por `alFinal`: referenciarlo obligaría a
    // declararlo como dependencia, y como se recrea en cada render el efecto
    // correría siempre en vez de una sola vez al montar.
    finDelHilo.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, []);

  function agregar(turno: Omit<Turno, 'id'>) {
    siguienteId.current += 1;
    const id = siguienteId.current;
    setTurnos((previos) => [...previos, { ...turno, id }]);
    requestAnimationFrame(alFinal);
  }

  const conversar = useMutation({
    mutationFn: (mensaje: string) => api.tenants.probar(tenantId, { sesionId, mensaje }),
    onSuccess: (r) => {
      setError(null);
      setTexto('');
      // El bot contestó: eso es haber probado. Va acá y no en `enviar` porque
      // un mensaje que falló no es una prueba.
      onProbado?.();
      setRestantes(r.restantes);
      // El servidor dice de qué herramientas dispone el bot; si el padre no lo
      // sabía, se aprende acá.
      if (r.herramientas?.length) setConQue(r.herramientas);
      agregar({
        de: 'bot',
        texto: r.respuesta,
        pregunta: ultimaPregunta.current,
        origen: r.origen,
        simuladas: r.simuladas,
        // Se avisa sólo si el bot YA había contestado antes. En el primer
        // mensaje la sesión es nueva por definición y decirlo asusta sin
        // motivo. Y no alcanza con `turnos.length > 0`: para cuando llega la
        // respuesta, el turno del propio dueño ya está en la lista.
        sesionNueva: r.sesionNueva && turnos.some((x) => x.de === 'bot'),
      });
    },
    // El texto NO se borra. Perder lo escrito ante un error es la clase de
    // detalle que hace abandonar una prueba, y esta pantalla existe para que
    // el dueño no la abandone.
    //
    // Y la burbuja SÍ se retira, por la misma razón: como el texto vuelve al
    // campo, dejarla haría que al reintentar el mensaje apareciera dos veces.
    // En la captura del PO se veía así — «Pueden bañar a mi perro ?» repetido
    // sin que él lo hubiera escrito dos veces. Un historial que muestra cosas
    // que no pasaron es lo contrario de para qué existe esta pantalla.
    onError: () => {
      setTurnos((previos) => previos.slice(0, -1));
      setError(t.noSePudo);
    },
  });

  function enviar() {
    const mensaje = texto.trim();
    if (!mensaje || conversar.isPending) return;
    ultimaPregunta.current = mensaje;
    agregar({ de: 'dueno', texto: mensaje });
    conversar.mutate(mensaje);
  }

  return (
    <section className="alta-chat" data-testid="probar-el-bot">
      <header className="probar-encabezado">
        <h2>{t.titulo}</h2>
        <p>{t.bajada}</p>
      </header>

      <div className="alta-chat-hilo" role="log" aria-live="polite" aria-label={t.titulo}>
        {turnos.length === 0 && (
          <div className="probar-vacio" data-testid="probar-vacio">
            <p>{t.vacio}</p>

            {/* Los casos que al dueño no se le ocurrirían (GKTIBO-76). */}
            <SugerenciasDePrueba servicios={servicios} herramientas={conQue} onElegir={setTexto} />
          </div>
        )}

        {turnos.map((turno) => (
          <div key={turno.id} className={`alta-chat-turno${turno.de === 'dueno' ? ' es-mio' : ''}`}>
            <div className="alta-chat-cuerpo">
              {turno.sesionNueva && (
                <p className="probar-aviso" data-testid="probar-sesion-nueva" role="note">
                  {t.sesionNueva}
                </p>
              )}

              {/* Lo del dueño es texto plano; lo del bot puede traer negritas
                  y viñetas, y mostrarlas crudas le impide juzgar cómo queda
                  (GKTIBO-114). */}
              <div className="alta-chat-burbuja">
                {turno.de === 'bot' ? <TextoDelBot texto={turno.texto} /> : turno.texto}
              </div>

              {turno.origen && <DeDondeSalio origen={turno.origen} />}

              {/* Corregir va con la respuesta, no en un panel aparte: el
                  momento de enseñar es cuando ves el error (GKTIBO-77). */}
              {turno.de === 'bot' && turno.pregunta && (
                <ValorarRespuesta
                  tenantId={tenantId}
                  pregunta={turno.pregunta}
                  respuesta={turno.texto}
                />
              )}

              {turno.simuladas?.map((s) => (
                <p
                  key={s.herramienta}
                  className="probar-origen es-simulado"
                  data-testid="probar-simulado"
                  role="note"
                >
                  {t.simulado} {s.enVivo}.
                </p>
              ))}
            </div>
          </div>
        ))}

        {conversar.isPending && (
          // `<output>` en vez de un `<p role="status">`: trae ese rol de
          // fábrica, y el lint del proyecto lo exige con razón — un elemento
          // semántico no depende de que nadie borre el atributo.
          // Que alguien con lector de pantalla sepa que el bot está pensando;
          // una animación sola no se oye.
          <output className="probar-pensando" data-testid="probar-pensando">
            <Sparkles className="probar-pensando-icono" aria-hidden="true" size={14} />
            {t.pensando}
          </output>
        )}
        <div ref={finDelHilo} />
      </div>

      {error && (
        <p className="alta-chat-error" role="alert">
          {error}
        </p>
      )}

      {restantes !== null && <p className="probar-cuota">{t.quedan(restantes)}</p>}

      <form
        className="alta-chat-redactor"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        <label className="sr-only" htmlFor="probar-entrada">
          {t.titulo}
        </label>
        <textarea
          id="probar-entrada"
          className="probar-entrada-que-crece"
          value={texto}
          // Crece con el texto en vez de quedarse en una línea: quien prueba
          // escribe párrafos, no consultas de una línea.
          rows={Math.min(6, texto.split('\n').length)}
          placeholder={t.escribi}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
        />
        {/* El ícono se AGREGA, la palabra no se quita: un botón que es sólo
            un ícono no se puede encontrar por su nombre con un lector de
            pantalla. */}
        <Button
          type="submit"
          disabled={conversar.isPending}
          trailingIcon={<SendHorizontal size={16} aria-hidden="true" />}
        >
          {t.enviar}
        </Button>
      </form>
    </section>
  );
}
